<template>
  <v-sheet
    class="plannings-order"
    :class="classes"
    :color="color"
    v-bind="$attrs"
  >
    <h3 v-if="showTitle" class="plannings-order__title text-h6">
      <slot name="title" :item="order">
        {{ $t('PLANNINGS.ORDER_DETAILS') }}:
      </slot>
    </h3>

    <p v-if="showFields.includes('order_number')" class="plannings-order__item">
      <strong>{{ $t('ORDERS.ITEM') }}:</strong>

      <router-link
        v-if="order.id"
        class="text-decoration-none"
        target="_blank"
        :to="orderRoute(order)"
      >
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            {{ order.order_number }}
            <v-icon small v-on="on">mdi-eye-outline</v-icon>
          </template>
          <span v-if="order.order_number">{{ order.order_number }}</span>
          <span v-else>{{ $t('ITEM.VIEW') }}</span>
        </v-tooltip>
      </router-link>
    </p>
    <p v-if="showFields.includes('customer')" class="plannings-order__item">
      <strong>{{ $t('ORDERS.CUSTOMER') }}:</strong>
      {{ order.customer || '' }}
    </p>
    <p v-if="showFields.includes('address')" class="plannings-order__item">
      <strong>{{ $t('ITEM.ADDRESS') }}:</strong>
      {{ address }}
    </p>
    <p v-if="showFields.includes('mobile_phone')" class="plannings-order__item">
      <strong>{{ $t('PROFILE.MOBILE_PHONE') }}:</strong>
      {{ order.customer_profile ? order.customer_profile.mobile_phone : '' }}
    </p>
    <p v-if="showFields.includes('phone')" class="plannings-order__item">
      <strong>{{ $t('PROFILE.PHONE') }}:</strong>
      {{ order.customer_profile ? order.customer_profile.phone : '' }}
    </p>
    <p v-if="showFields.includes('email')" class="plannings-order__item">
      <strong>{{ $t('PROFILE.EMAIL') }}:</strong>
      {{ order.customer_profile ? order.customer_profile.email : '' }}
    </p>
    <p v-if="showFields.includes('service')" class="plannings-order__item">
      <strong>{{ $t('SERVICES.ITEM') }}:</strong>
      {{ service }}
    </p>
    <p
      v-if="showFields.includes('dateServiceExpected')"
      class="plannings-order__item"
    >
      <strong>{{ $t('ORDERS.DATE_SERVICE_EXPECTED') }}:</strong>
      {{ dateServiceExpected }}
    </p>
    <p v-if="showFields.includes('duration')" class="plannings-order__item">
      <strong>{{ $t('ORDERS.DURATION') }}:</strong>
      {{ order.duration || '' }}
    </p>
    <p
      v-if="showFields.includes('key_deposited')"
      class="plannings-order__item"
    >
      <strong>{{ $t('ORDERS.KEY_DEPOSITED') }}:</strong>
      {{ order.key_deposited || '' }}
    </p>
    <p
      v-if="showFields.includes('substitution_options')"
      class="plannings-order__item"
    >
      <strong>{{ $t('ORDERS.SUBSTITUTION_OPTIONS') }}:</strong>
      {{ substitutionOptionsName }}
    </p>
    <div
      v-if="showFields.includes('description')"
      class="plannings-order__item"
    >
      <strong>{{ $t('ITEM.DESCRIPTION') }}:</strong>
      <span
        v-if="order.description"
        class="d-block pa-2 rounded grey lighten-5"
      >
        {{ order.description }}
      </span>
    </div>
  </v-sheet>
</template>

<script>
import { getViewRouteNameByOrderType } from '@/modules/orders/common/utils';
import { dateFormat } from '@/modules/_global/filters';
import { mapCacheActions } from 'vuex-cache';
import i18nRefreshData from '@/modules/_global/mixins/i18nRefreshData';

export default {
  name: 'OrderSheet',

  filters: {
    dateFormat
  },

  mixins: [i18nRefreshData],

  props: {
    order: {
      type: Object,
      default: () => ({ customer_profile: {} })
    },
    color: {
      type: String,
      default: 'grey lighten-4'
    },
    showTitle: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    showFields: {
      type: Array,
      default() {
        return [
          'order_number',
          'customer',
          'address',
          'service',
          'dateServiceExpected',
          'duration',
          'substitution_options',
          'key_deposited',
          'description'
        ];
      }
    }
  },

  data() {
    return { substitutionOptions: [] };
  },

  computed: {
    classes() {
      return {
        'plannings-order_small': this.small
      };
    },
    address() {
      const street = this.order.street || '';
      const zip = this.order.zip || '';
      const city = this.order.city || '';

      return `${street}, ${zip} ${city}`.replace(/^[\s,]+|[\s,]+$/g, '');
    },
    substitutionOptionsName() {
      if (this.order.substitution_options)
        return (
          this.order.substitution_options
            .map(
              option =>
                (this.substitutionOptions.find(el => el.id === option) || {})
                  .name
            )
            .join(', ') || ''
        );
      else return '';
    },
    service() {
      const service = this.order.service;

      return (service && service.translation && service.translation.name) || '';
    },
    dateServiceExpected() {
      const date = this.order.date_service_expected;
      return date ? dateFormat(date) : '';
    }
  },

  mounted() {
    this.refreshData();
  },

  methods: {
    ...mapCacheActions('order', ['getOrderSubstitutionOptions']),
    refreshData() {
      this.getOrderSubstitutionOptions().then(({ data }) => {
        this.substitutionOptions = data.data;
      });
    },
    orderRoute(order) {
      const routeName = getViewRouteNameByOrderType(order.type_id);

      if (routeName) {
        return {
          name: routeName,
          params: { id: order.id }
        };
      }
    }
  }
};
</script>

<style lang="scss">
.plannings-order {
  &__item,
  &__title {
    display: block;
    margin-bottom: 1rem;
  }

  &_small &__item,
  &_small &__title {
    display: block;
    margin-bottom: 0.4rem;
  }

  & &__item {
    font-size: 1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
